import { ChevronRight } from "@/components/ui/icons";
import { Pressable, Text, View } from "react-native";

import type { PublicLiveTable } from "@/hooks/usePublicBrowse";
import { useThemeColors } from "@/theme/theme";

/**
 * Says on the vendor's shop that they are at a show right now, and takes the customer to that
 * table (ADR-0138).
 *
 * It replaced an "At the table / Shop" tab bar. The two were never peers: the table is a live,
 * physical, time-boxed room with its own pick list, the shop the always-open book, so a muted tab
 * undersold the most urgent fact on the page, and tabs that navigate away are links wearing a tab
 * role. It sits in the pinned part of the header, so a customer deep in the book can still cross.
 *
 * With one table live it opens that table. With several it opens the chooser, since the tables
 * hold different stock and a guess would send the customer to one they cannot buy from.
 */
export function LiveTableBanner(
  { tables, onOpen, onChoose }: {
    tables: readonly PublicLiveTable[];
    onOpen: (eventId: string) => void;
    /** Asks which table, for a vendor running more than one. */
    onChoose: () => void;
  },
) {
  const c = useThemeColors();
  if (tables.length === 0) return null;
  const one = tables.length === 1 ? tables[0] : null;
  const where = one ? one.name : `${tables.length} tables`;
  return (
    <Pressable
      testID="live-table-banner"
      onPress={() => (one ? onOpen(one.event_id) : onChoose())}
      accessibilityRole="link"
      accessibilityLabel={one
        ? `Live now at ${one.name}. Shop this table`
        : `Live now at ${where}. Choose a table`}
      className="min-h-11 flex-row items-center gap-2 rounded-xl border border-primary bg-accent-soft px-3 py-2"
    >
      <View aria-hidden className="h-2 w-2 rounded-full bg-primary" />
      <Text className="min-w-0 flex-1 font-body text-sm text-foreground" numberOfLines={1}>
        <Text className="font-label text-primary">Live now</Text> · {where}
      </Text>
      <Text className="font-label text-sm text-primary">
        {one ? "Shop this table" : "Choose"}
      </Text>
      <ChevronRight size={16} color={c.primary} aria-hidden />
    </Pressable>
  );
}
